<script>
// Main Components
import Layout from '@/router/layouts/main';
import Grid from './components/grid.vue';
import FiltersCard from '@/components/widgets/cards/filters-search';

// Repositories
import Repositories from '@/repositories';
const StepsRepository = Repositories.get('steps');

//Other components
import Multiselect from 'vue-multiselect';

export default {
  page: {
    title: 'Categories',
  },
  components: {
    Layout,
    Grid,
    FiltersCard,
    Multiselect,
  },
  data() {
    return {
      isLoading: false,
      steps: [],
      categories: [],
      filters: null,
      searchText: '',
      selectedCategoryTypes: [],
      selectedConsumptionSteps: [],
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      let promise3 = StepsRepository.getAll().then((response) => {
        this.steps = response.data.data.sort(function (a, b) {
          return a.generalOrder - b.generalOrder;
        });
      });

      Promise.all([promise3])
        .then(() => {
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    search() {
      this.filters = {
        categoryTypesIdPublic: this.selectedCategoryTypeIds,
        consumptionStepsIdPublic: this.selectedConsumptionStepIds,
        search: this.searchText,
      };
    },
    cancelSearch() {
      this.searchText = '';
      this.selectedCategoryTypes = [];
      this.selectedConsumptionSteps = [];
      this.search();
    },
    create() {
      this.$router.push({ name: 'createCategory' });
    },
  },
  computed: {
    selectedCategoryTypeIds() {
      return this.selectedCategoryTypes != null
        ? this.selectedCategoryTypes.map((a) => a.idPublic)
        : [];
    },
    selectedConsumptionStepIds() {
      return this.selectedConsumptionSteps != null
        ? this.selectedConsumptionSteps.map((a) => a.idPublic)
        : [];
    },
    fieldsComputed() {
      return [
        {
          key: 'name',
          label: this.$t('general.name'),
          sortable: true,
        },
        {
          key: 'categoryTypeName',
          label: this.$t('categories.categoryType'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'consumptionStepName',
          label: this.$t('categories.consumptionStep'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'referenceDate',
          label: this.$t('general.referenceDate'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'leadTime',
          label: this.$t('general.leadTime'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'edit',
          label: this.$t('general.edit'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'delete',
          label: this.$t('general.delete'),
          class: 'centered-field',
          sortable: true,
        },
      ];
    },
    categoryTypes() {
      return this.$store.state.cache.categoryTypes;
    },
  },
};
</script>

<template>
  <Layout
    :title="$t('categories.categories')"
    :no-card="true"
    @esc-double-pressed="cancelSearch"
  >
    <div class="flex-box">
      <b-input-group class="search-bar custom-inventory-shadow">
        <b-form-input
          id="searchInput"
          type="text"
          style="border: none"
          :placeholder="$t('general.search')"
          @keydown.escape.prevent="cancelSearch()"
          @keydown.enter.prevent="search()"
          v-model="searchText"
        ></b-form-input>
        <b-input-group-append>
          <b-button
            style="border: none"
            variant="outline-light"
            @click="search()"
          >
            <i class="bx bx-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <div class="flex-push">
        <b-button @click="create()" variant="primary">
          {{ $t('general.add') }}
        </b-button>
      </div>
    </div>

    <FiltersCard @search="search" @cancel="cancelSearch">
      <template #row>
        <b-row>
          <b-col sm="12" lg="3">
            <b-form-group
              id="group-category-types"
              :label="$t('categories.categoryTypes')"
              label-class="no-bold"
              label-for="categoryTypeSelect"
            >
              <multiselect
                id="categoryTypeSelect"
                ref="multiselectCategoryType"
                v-model="selectedCategoryTypes"
                :options="categoryTypes"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectCategoryType)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>
          <b-col sm="12" lg="3">
            <b-form-group
              id="group-consumption-steps"
              :label="$t('categories.consumptionStep')"
              label-class="no-bold"
              label-for="consumptionStepSelect"
            >
              <multiselect
                id="consumptionStepSelect"
                ref="multiselectConsumptionStep"
                v-model="selectedConsumptionSteps"
                :options="steps"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectConsumptionStep)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>
        </b-row>
      </template>
    </FiltersCard>

    <Grid :filters="filters" :fields="fieldsComputed" />
  </Layout>
</template>

<style scoped>
.search-bar {
  max-width: 500px;
  max-height: 40px;
  background-color: white;
  border-radius: 5px;
}
</style>
